export type CrowdinThemeMode = 'light' | 'dark';

type CssVariables = Record<string, string>;

interface CrowdinHost {
    getTheme?: (callback: (theme: string) => void) => void;
    getCssVariables?: (callback: (variables: CssVariables) => void) => void;
    events?: {
        on?: (event: string, callback: (data?: unknown) => void) => void;
    };
}

let appliedHostVariables = new Set<string>();

// Deliberately not a global Window augmentation: consumers commonly declare
// their own AP global and interface merging would conflict on any type
// mismatch. A local cast keeps this file free of global-scope footprint.
function host(): CrowdinHost | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    return (window as Window & { AP?: CrowdinHost }).AP;
}

export function applyCrowdinCssVariables() {
    host()?.getCssVariables?.((variables) => {
        const nextHostVariables = new Set<string>();

        for (const [name, value] of Object.entries(variables)) {
            // The --color-* passthrough mirrors the apps SDK on purpose: it lets
            // the host pin Tailwind-level tokens directly, at the cost of such a
            // host value overriding a local @theme name if the two ever collide.
            if (name.startsWith('--crowdin-') || name.startsWith('--color-')) {
                nextHostVariables.add(name);
                document.documentElement.style.setProperty(name, value);
            }
        }

        for (const name of appliedHostVariables) {
            if (!nextHostVariables.has(name)) {
                document.documentElement.style.removeProperty(name);
            }
        }

        appliedHostVariables = nextHostVariables;
    });
}

// iframe.js is typically loaded async, so AP may appear after the app
// mounts; probe for a while before concluding we are outside the iframe.
const PROBE_INTERVAL_MS = 250;
const PROBE_LIMIT = 40;

// A single process-wide theme store, not one subscription per consumer. A page can
// mount several React roots (the form page renders one for the form and one for the
// toaster, and re-creates the form root on every render), and giving each its own
// state meant they could disagree - the form showed a light Monaco on a dark host -
// besides calling AP.getTheme once per root.
type CrowdinThemeListener = (mode: CrowdinThemeMode) => void;

const themeListeners = new Set<CrowdinThemeListener>();
let currentThemeMode: CrowdinThemeMode = 'light';
let hostWiringStarted = false;

function setThemeMode(mode: CrowdinThemeMode) {
    if (mode === currentThemeMode) {
        return;
    }

    currentThemeMode = mode;
    themeListeners.forEach((listener) => listener(mode));
}

// Never torn down: AP offers no way to unsubscribe, and the host palette should stay
// applied for as long as the page lives.
function startHostWiring() {
    if (hostWiringStarted || typeof window === 'undefined') {
        return;
    }
    hostWiringStarted = true;

    const wire = (ap: CrowdinHost) => {
        const sync = () => {
            ap.getTheme?.((theme) => setThemeMode(theme === 'dark' ? 'dark' : 'light'));
            applyCrowdinCssVariables();
        };
        sync();
        ap.events?.on?.('theme.changed', sync);
    };

    const probe = (attemptsLeft: number) => {
        const ap = host();
        if (ap?.getTheme) {
            wire(ap);
        } else if (attemptsLeft > 0) {
            setTimeout(() => probe(attemptsLeft - 1), PROBE_INTERVAL_MS);
        }
    };

    probe(PROBE_LIMIT);
}

export function getCrowdinThemeMode(): CrowdinThemeMode {
    return currentThemeMode;
}

// Reports the host theme mode now and on every host theme change. Outside the
// Crowdin iframe it stays on the default and never fires.
export function subscribeCrowdinTheme(listener: CrowdinThemeListener) {
    themeListeners.add(listener);
    startHostWiring();

    return () => {
        themeListeners.delete(listener);
    };
}
